import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';
import { getPost, BLOG_POSTS } from '../data/blogPosts';

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Organization', name: 'MoneyFeed' },
    publisher: { '@type': 'Organization', name: 'MoneyFeed', logo: { '@type': 'ImageObject', url: 'https://moneyfeed.io/logo512.png' } },
    mainEntityOfPage: `https://moneyfeed.io/blog/${post.slug}`,
  };

  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  const isAr = post.lang === 'ar';

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-screen bg-white" data-testid="blog-post-page">
      <Seo title={`${post.title} | MoneyFeed`} description={post.description} path={`/blog/${post.slug}`} jsonLd={jsonLd} {...(post.lang ? { lang: post.lang, dir: isAr ? 'rtl' : 'ltr' } : {})} />

      <header className="flex items-center justify-between px-4 sm:px-8 py-4 max-w-6xl mx-auto">
        <Link to="/" className="flex items-center"><img src="/logo-mark.png" alt="MoneyFeed" className="h-9 w-auto" /></Link>
        <Link to="/auth"><Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-5" data-testid="post-signup-btn">Track it free</Button></Link>
      </header>

      <article className="max-w-2xl mx-auto px-4 sm:px-6 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#064E3B] text-sm mb-6" data-testid="post-back-link">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
          <span>{new Date(post.date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          <span>·</span>
          <span>{post.readTime}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#064E3B] mb-6 leading-tight">{post.title}</h1>

        <div dangerouslySetInnerHTML={{ __html: post.html }} />

        <div className="mt-10 bg-[#064E3B] rounded-2xl p-6 text-center">
          <p className="text-white font-bold text-lg mb-1">Do your bookkeeping from WhatsApp</p>
          <p className="text-[#A7F3D0] text-sm mb-4">Text your expenses, track mileage and get tax-ready reports — automatically.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-6" data-testid="post-cta-btn">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="mt-12">
          <h3 className="text-lg font-bold text-[#064E3B] mb-4">Keep reading</h3>
          <div className="space-y-3">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="block text-[#0a6b52] font-semibold hover:underline" data-testid={`related-${p.slug}`}>
                {p.title}
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
